import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { Constants } from 'src/app/app.constants';
import { AuthService, MerchantService } from 'src/app/services';
import { VoucherService } from 'src/app/services/voucher.service';
import { Voucher } from 'src/app/models/voucher';
import { MatDialog } from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { VoucherLibraryDialogComponent } from '../voucher-library-dialog/voucher-library-dialog.component';
import { SingleInputDialogComponent } from 'src/app/shared/components/single-input-dialog/single-input-dialog.component';
import { TextEditorComponent } from 'src/app/shared/components/text-editor/text-editor.component';

interface VoucherPayload {
  title?: string;
  description?: string;
  value?: number;
  cost?: number;
  expiryDays?: number;
  quantityLimit?: number | null;
  terms?: string;
  allowPartialRedemption: boolean;
  showValueInDisplay: boolean;
  displayOrder?: number;
  id?: string;
  coverImageOptions: 'UPLOADED_ONLY' | 'UPLOADED_AND_LIBRARY';
  visibility: ('app' | 'web')[];
  cover?: { name: string; content: ArrayBuffer; mime: string } | null;
  emailArtwork?: { name: string; content: ArrayBuffer; mime: string } | null;
  monetaryOptions?: {
    min?: number;
    max?: number;
    increment?: number;
  };
  voucherType: 'fixed' | 'monetary';
}
@Component({
  selector: 'gift-voucher-detail',
  templateUrl: './gift-voucher-detail.component.html',
  styleUrls: ['./gift-voucher-detail.component.css'],
})
export class GiftVoucherDetailComponent implements OnInit {
  minimumCost = 10;
  minValue = 10;
  minimumListingCost = 10;
  hasExpiry = true;
  loading = false;
  saving = false;
  previewingPDF = false;
  submitted = false;
  editMode = false;
  voucher: any = {};
  canCreateVoucher = false;
  canEditVoucher = false;
  form!: FormGroup;
  planLimitsLoading = false;
  locationsLoading: boolean = false;
  subtitle: string = '';
  titleFormControl = new FormControl('', [Validators.required]);
  descriptionFormControl = new FormControl('');
  voucherTypeFormControl = new FormControl<any>(null, [Validators.required]);
  valueFormControl = new FormControl('');
  costFormControl = new FormControl('');
  minFormControl = new FormControl<any>(null);
  maxFormControl = new FormControl<any>(null);
  incrementFormControl = new FormControl<any>(null);
  expiryDaysFormControl = new FormControl('', [Validators.min(1)]);
  quantityLimitFormControl = new FormControl('');
  allowPartialRedemptionControl = new FormControl(false);
  showValueInDisplayControl = new FormControl(true);
  visibilityFormControl = new FormControl<any[]>(
    [],
    [Validators.required, this.visibilityValidator],
  );

  locations: any[] = [];
  planLimits: any;
  error = false;
  disabledLocations: boolean[] = [];
  selectedCover?: [{ name: string; content: ArrayBuffer; mime: string }];
  initialCoverUrl?: string | null;
  selectedEmailArtwork?: [{ name: string; content: ArrayBuffer; mime: string }];
  initialEmailArtworkUrl?: string | null;
  hasVoucherSpecificEmailArtwork: boolean = false;
  emailArtworkDeleteRequested: boolean = false;
  terms = '';

  @ViewChild('termsEditor') termsEditor?: TextEditorComponent;

  coverOptionsFormControl = new FormControl('UPLOADED_AND_LIBRARY');

  visibilityOptions = [
    { value: 'app', label: 'Mobile App' },
    { value: 'web', label: 'Web/Microsite' },
  ];

  voucherTypeOptions: { label: string; value: string; info: string }[] = [
    {
      label: 'Fixed',
      value: 'fixed',
      info: 'E.g. afternoon tea voucher',
    },
    {
      label: 'Monetary',
      value: 'monetary',
      info: `E.g. £40 voucher`,
    },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private merchantService: MerchantService,
    public constants: Constants,
    private voucherService: VoucherService,
    private authService: AuthService,
    private dialog: MatDialog,
  ) {
    this.route.params.subscribe(
      (params) => (this.voucher.nanoid = params['id']),
    );

    if (this.voucher.nanoid) {
      this.editMode = true;
    }
  }

  private valueCostValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      return null;
    };
  }

  private visibilityValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = control.value;
    if (!value || !Array.isArray(value) || value.length === 0) {
      return { required: true };
    }
    // Check if all selected items have valid values
    const hasValidValues = value.every((item) => item && item.value);
    if (!hasValidValues) {
      return { required: true };
    }
    return null;
  }

  ngOnInit() {
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');

    if (!this.editMode) {
      this.getPlanLimits();
      this.coverOptionsFormControl.setValue('UPLOADED_AND_LIBRARY');
      // Set default visibility to both app and web
      this.visibilityFormControl.setValue([
        { value: 'app', label: 'Mobile App' },
        { value: 'web', label: 'Web/Microsite' },
      ]);
      // Set default voucher type to 'fixed'
      this.voucherTypeFormControl.setValue(
        this.voucherTypeOptions.find((option) => option.value === 'fixed'),
      );
      this.prePopulateCoverImage();
    } else {
      this.getVoucher();
    }

    // Set default to UPLOADED_AND_LIBRARY if no cover is uploaded
    this.coverOptionsFormControl.valueChanges.subscribe((value) => {
      if (value === 'UPLOADED_ONLY' && !this.hasUploadedCover) {
        this.coverOptionsFormControl.setValue('UPLOADED_AND_LIBRARY');
      }
    });

    this.form = this.fb.group(
      {
        title: this.titleFormControl,
        description: this.descriptionFormControl,
        value: this.valueFormControl,
        cost: this.costFormControl,
        expiryDays: this.expiryDaysFormControl,
        quantityLimit: this.quantityLimitFormControl,
        allowPartialRedemption: this.allowPartialRedemptionControl,
        showValueInDisplay: this.showValueInDisplayControl,
        coverOptions: this.coverOptionsFormControl,
        visibility: this.visibilityFormControl,
        voucherType: this.voucherTypeFormControl,
        min: this.minFormControl,
        max: this.maxFormControl,
        increment: this.incrementFormControl,
      },
      {
        validators: this.valueCostValidator(),
      },
    );

    // Subscribe to value changes to revalidate when either field changes
    this.valueFormControl.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });

    this.costFormControl.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });

    // Set up validators based on voucher type (for new vouchers)
    if (!this.editMode) {
      this.voucherTypeSelected();
    }
  }

  getPlanLimits(): void {
    this.planLimitsLoading = true;
    this.merchantService
      .getPlanLimits()
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.planLimits = res;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => {
        this.planLimitsLoading = false;
      });
  }

  prePopulateCoverImage(): void {
    this.voucherService.getVouchers(50, 0).subscribe({
      next: (res: any) => {
        if (res && res.docs && res.docs.length > 0) {
          const voucherWithCover = res.docs.find(
            (voucher: any) => voucher.cover && voucher.cover.url,
          );

          if (voucherWithCover) {
            this.initialCoverUrl =
              voucherWithCover.cover.url + '?' + new Date().getTime();
          }
        }
      },
      error: (res: HttpErrorResponse) => {
        console.log('No existing voucher with cover found');
      },
    });
  }

  populateForm() {
    this.voucherTypeFormControl.setValue(
      this.voucherTypeOptions.find(
        (option) => option.value === (this.voucher.voucherType ?? 'fixed'),
      ),
    );

    this.titleFormControl.setValue(this.voucher.title);
    this.descriptionFormControl.setValue(this.voucher.description);
    this.valueFormControl.setValue(this.voucher.value);
    this.costFormControl.setValue(this.voucher.cost);
    this.expiryDaysFormControl.setValue(this.voucher.expiryDays);
    this.quantityLimitFormControl.setValue(this.voucher.quantityLimit);
    this.terms = this.voucher.terms ?? '';

    this.initialCoverUrl = this.voucher.cover?.url
      ? this.voucher.cover?.url + '?' + new Date().getTime()
      : null;

    const merchant = this.authService.merchantObj;
    const micrositeEmailArtworkUrl =
      merchant?.microsite?.branding?.emailArtwork?.url;

    this.hasVoucherSpecificEmailArtwork = !!this.voucher.emailArtwork?.url;
    this.emailArtworkDeleteRequested = false;

    this.initialEmailArtworkUrl = this.voucher.emailArtwork?.url
      ? this.voucher.emailArtwork?.url + '?' + new Date().getTime()
      : micrositeEmailArtworkUrl
        ? micrositeEmailArtworkUrl + '?' + new Date().getTime()
        : null;

    // Set the cover options value from the API
    if (this.voucher.coverImageOptions) {
      this.coverOptionsFormControl.setValue(this.voucher.coverImageOptions);
    }

    if (!this.voucher.expiryDays || this.voucher.expiryDays === 0) {
      this.hasExpiry = false;
    }

    if (this.hasExpiry) {
      this.expiryDaysFormControl.setValidators([Validators.required]);
    } else {
      this.expiryDaysFormControl.clearValidators();
    }

    this.allowPartialRedemptionControl.setValue(
      this.voucher.allowPartialRedemption,
    );

    this.showValueInDisplayControl.setValue(
      this.voucher.showValueInDisplay !== undefined
        ? this.voucher.showValueInDisplay
        : true,
    );

    // Set visibility with default fallback - convert API values to objects
    const visibilityValues =
      this.voucher.visibility && this.voucher.visibility.length > 0
        ? this.voucher.visibility
        : ['app', 'web'];

    const visibilityObjects = visibilityValues
      .map((value: string) =>
        this.visibilityOptions.find((option) => option.value === value),
      )
      .filter(Boolean);

    this.visibilityFormControl.setValue(visibilityObjects);

    if (this.voucher.voucherType === 'monetary') {
      this.minFormControl.setValue(this.voucher.monetaryOptions?.min);
      this.maxFormControl.setValue(this.voucher.monetaryOptions?.max);
      this.incrementFormControl.setValue(
        this.voucher.monetaryOptions?.increment,
      );
    } else {
      this.valueFormControl.setValue(this.voucher.value);
      this.costFormControl.setValue(this.voucher.cost);
    }

    // Set up validators based on voucher type
    this.voucherTypeSelected();
  }

  save() {
    this.submitted = true;

    // Additional validation for voucher type specific fields
    let hasValidationErrors = false;

    if (this.voucherType === 'fixed') {
      if (
        this.valueFormControl.value === null ||
        this.valueFormControl.value === undefined ||
        this.valueFormControl.value === ''
      ) {
        this.valueFormControl.markAsTouched();
        hasValidationErrors = true;
      } else {
        const valueNum = +this.valueFormControl.value;
        if (isNaN(valueNum) || valueNum < 0) {
          this.valueFormControl.markAsTouched();
          hasValidationErrors = true;
        }
      }
      if (
        !this.costFormControl.value ||
        +this.costFormControl.value < this.minimumCost
      ) {
        this.costFormControl.markAsTouched();
        hasValidationErrors = true;
      }
    } else if (this.voucherType === 'monetary') {
      if (
        !this.minFormControl.value ||
        +this.minFormControl.value < this.minValue
      ) {
        this.minFormControl.markAsTouched();
        hasValidationErrors = true;
      }
      if (
        !this.maxFormControl.value ||
        +this.maxFormControl.value < this.minValue
      ) {
        this.maxFormControl.markAsTouched();
        hasValidationErrors = true;
      }
      if (
        !this.incrementFormControl.value ||
        +this.incrementFormControl.value < 1
      ) {
        this.incrementFormControl.markAsTouched();
        hasValidationErrors = true;
      }
    }

    if (this.form.invalid || hasValidationErrors) {
      this.form.markAllAsTouched();
      Object.values(this.form.controls).forEach((control) => {
        if (control.invalid) {
          console.log(
            `Control: ${Object.keys(this.form.controls).find(
              (key) => this.form.controls[key] === control,
            )}, Errors: ${JSON.stringify(control.errors)}`,
          );
        }
      });
      // Check for specific validation errors to provide better feedback
      if (this.voucherType === 'fixed') {
        if (
          this.valueFormControl.value === null ||
          this.valueFormControl.value === undefined ||
          this.valueFormControl.value === ''
        ) {
          this.constants.snack('Redeemable value is required');
        } else {
          const valueNum = +this.valueFormControl.value;
          if (isNaN(valueNum) || valueNum < 0) {
            this.constants.snack('Redeemable value must be £0 or greater');
          } else if (
            !this.costFormControl.value ||
            +this.costFormControl.value < this.minimumCost
          ) {
            this.constants.snack(`Cost must be at least £${this.minimumCost}`);
          } else {
            this.constants.snack(
              'Please complete all required fields correctly',
            );
          }
        }
      } else if (this.voucherType === 'monetary') {
        if (
          !this.minFormControl.value ||
          +this.minFormControl.value < this.minValue
        ) {
          this.constants.snack(
            `Minimum value must be at least £${this.minValue}`,
          );
        } else if (
          !this.maxFormControl.value ||
          +this.maxFormControl.value < this.minValue
        ) {
          this.constants.snack(
            `Maximum value must be at least £${this.minValue}`,
          );
        } else if (
          !this.incrementFormControl.value ||
          +this.incrementFormControl.value < 1
        ) {
          this.constants.snack('Increment must be at least £1');
        } else {
          this.constants.snack('Please complete all required fields correctly');
        }
      } else {
        this.constants.snack('Please complete all required fields correctly');
      }
      this.form.markAsDirty();
      return;
    }

    let payload: VoucherPayload = {
      title: this.titleFormControl.value?.trim(),
      description: this.descriptionFormControl.value?.trim(),
      expiryDays:
        this.hasExpiry === true
          ? this.expiryDaysFormControl.value
            ? +this.expiryDaysFormControl.value
            : undefined
          : undefined,
      terms:
        this.termsEditor?.getSanitizedAndLinkifiedContent() ||
        this.terms?.trim(),
      allowPartialRedemption: this.allowPartialRedemptionControl.value ?? false,
      showValueInDisplay: this.showValueInDisplayControl.value ?? true,
      id: this.editMode ? this.voucher._id : undefined,
      coverImageOptions: this.coverOptionsFormControl.value as
        | 'UPLOADED_ONLY'
        | 'UPLOADED_AND_LIBRARY',
      visibility: (() => {
        const selectedValues = (this.visibilityFormControl.value || [])
          .map((option: any) => option.value)
          .filter(Boolean);
        return selectedValues.length > 0 ? selectedValues : ['app', 'web'];
      })() as ('app' | 'web')[],
      voucherType: this.voucherType as 'fixed' | 'monetary',
    };

    if (payload.voucherType === 'monetary') {
      payload.monetaryOptions = {
        min: this.minFormControl.value ?? 10,
        max: this.maxFormControl.value ?? 100,
        increment: this.incrementFormControl.value ?? 10,
      };
    } else {
      payload.value = +(this.valueFormControl.value ?? 0);
      payload.cost = +(this.costFormControl.value ?? 0);
      payload.quantityLimit = this.quantityLimitFormControl.value
        ? +this.quantityLimitFormControl.value
        : null;
    }

    if (this.selectedCover?.[0]) {
      payload.cover = this.selectedCover[0];
    } else if (this.initialCoverUrl === null) {
      payload.cover = null; // Explicit deletion
    }

    if (this.selectedEmailArtwork?.[0]) {
      payload.emailArtwork = this.selectedEmailArtwork[0];
    } else if (this.emailArtworkDeleteRequested) {
      payload.emailArtwork = null; // Explicit deletion
    }

    this.saving = true;

    if (this.editMode) {
      this.voucherService
        .editVoucher(payload)
        .subscribe(this.handleResponse)
        .add(() => (this.saving = false));
    } else {
      this.voucherService
        .createVoucher(payload)
        .subscribe(this.handleResponse)
        .add(() => (this.saving = false));
    }
  }

  private handleResponse = {
    next: (res: any) => {
      if (res) {
        this.router.navigate([Constants.routes.giftVouchers]);
      }
    },
    error: (res: HttpErrorResponse) => {
      this.constants.snack(res.error.message);
    },
    complete: () => {
      this.saving = false;
    },
  };

  back() {
    this.router.navigate([Constants.routes.giftVouchers]);
  }

  get tooltipLabel(): string {
    if (this.loading) {
      return '';
    }
    if (!this.canCreateVoucher) {
      if (!this.locations.length) {
        return this.constants.strings.addLocation;
      } else {
        return this.constants.strings.upgradePlanNotice;
      }
    } else {
      return '';
    }
  }

  setCanEditVoucher() {
    this.canEditVoucher = (!this.editMode || this.voucher._canManage) ?? false;

    if (!this.canEditVoucher) {
      this.titleFormControl.disable();
      this.descriptionFormControl.disable();
      this.valueFormControl.disable();
      this.expiryDaysFormControl.disable();
      this.visibilityFormControl.disable();
    }
  }

  getVoucher(): void {
    this.loading = true;
    this.voucherService
      .getVoucher(this.voucher.nanoid)
      .subscribe({
        next: (res: Voucher) => {
          if (res) {
            this.voucher = res;
            this.populateForm();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.router
            .navigate(['/' + Constants.routes.dashboard], {})
            .then(() => {
              this.router.navigate([Constants.routes.giftVouchers]);
            });
        },
      })
      .add(() => (this.loading = false));
  }

  previewPDF(): void {
    if (!this.voucher?.nanoid) {
      this.constants.snack('Unable to preview PDF - voucher not found');
      return;
    }

    this.previewingPDF = true;
    this.voucherService
      .previewVoucherPDF(this.voucher.nanoid)
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');

          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error?.message || 'Error opening PDF preview',
          );
        },
      })
      .add(() => (this.previewingPDF = false));
  }

  sendTestEmail(): void {
    if (!this.voucher?.nanoid) {
      this.constants.snack('Unable to send test email - voucher not found');
      return;
    }

    const dialogRef = this.dialog.open(SingleInputDialogComponent, {
      data: {
        title: 'Send Test Email',
        label: 'Email Address',
        value: '',
        maxLength: 100,
      },
      width: '400px',
      autoFocus: false,
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.changed && result?.value) {
        const email = result.value.trim();

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          this.constants.snack('Please enter a valid email address');
          return;
        }

        this.performSendTestEmail(email);
      }
    });
  }

  private performSendTestEmail(email: string): void {
    this.saving = true;
    this.voucherService
      .sendTestVoucherEmail(this.voucher.nanoid, email)
      .subscribe({
        next: () => {
          this.constants.snack(`Test email sent successfully to ${email}`);
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error?.message || 'Error sending test email',
          );
        },
      })
      .add(() => (this.saving = false));
  }

  deleteCover(): void {
    this.selectedCover = undefined;
    this.initialCoverUrl = null;
    this.coverOptionsFormControl.setValue('UPLOADED_AND_LIBRARY');
  }

  onEmailArtworkSelected(event: any): void {
    this.selectedEmailArtwork = event;
    this.hasVoucherSpecificEmailArtwork = true;
    this.emailArtworkDeleteRequested = false;
  }

  deleteEmailArtwork(): void {
    this.selectedEmailArtwork = undefined;
    this.hasVoucherSpecificEmailArtwork = false;
    this.emailArtworkDeleteRequested = true;

    const merchant = this.authService.merchantObj;
    const micrositeEmailArtworkUrl =
      merchant?.microsite?.branding?.emailArtwork?.url;
    this.initialEmailArtworkUrl = micrositeEmailArtworkUrl
      ? micrositeEmailArtworkUrl + '?' + new Date().getTime()
      : null;
  }

  openLibrary(event: Event): void {
    event.preventDefault();

    this.dialog.open(VoucherLibraryDialogComponent, {
      width: '800px',
      autoFocus: false,
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  get hasUploadedCover(): boolean {
    return !!(this.selectedCover || this.initialCoverUrl);
  }

  getDiscountPercentage(): number {
    const value = Number(this.valueFormControl.value) || 0;
    const cost = Number(this.costFormControl.value) || 0;

    if (value === 0 || isNaN(value) || isNaN(cost)) return 0;

    const percentage = ((value - cost) / value) * 100;
    return isNaN(percentage) ? 0 : Math.max(0, percentage);
  }

  toggleExpiry(): void {
    this.hasExpiry = !this.hasExpiry;

    if (this.hasExpiry) {
      this.expiryDaysFormControl.setValidators([
        Validators.required,
        Validators.min(1),
      ]);
    } else {
      this.expiryDaysFormControl.clearValidators();
      this.expiryDaysFormControl.setValue(null);
    }

    this.expiryDaysFormControl.updateValueAndValidity();
  }

  getValueError(): string {
    if (this.valueFormControl.hasError('required')) {
      return 'Redeemable value is required';
    }
    if (this.valueFormControl.hasError('min')) {
      return 'Redeemable value must be £0 or greater';
    }
    return '';
  }

  getCostError(): string {
    if (this.costFormControl.hasError('required')) {
      return 'Cost is required';
    }
    if (this.costFormControl.hasError('min')) {
      return `Minimum cost is £${this.minimumCost}`;
    }
    return '';
  }

  getMinError(): string {
    if (this.minFormControl.hasError('required')) {
      return 'Min value is required';
    }
    if (this.minFormControl.hasError('min')) {
      return `Minimum value is £${this.minValue}`;
    }
    return '';
  }

  getMaxError(): string {
    if (this.maxFormControl.hasError('required')) {
      return 'Max value is required';
    }
    if (this.maxFormControl.hasError('min')) {
      return `Maximum value must be at least £${this.minValue}`;
    }
    return '';
  }

  getIncrementError(): string {
    if (this.incrementFormControl.hasError('required')) {
      return 'Increment is required';
    }
    if (this.incrementFormControl.hasError('min')) {
      return 'Increment must be at least £1';
    }
    return '';
  }

  get voucherTypeTooltip(): string {
    return `Choose between a fixed value voucher or a monetary voucher`;
  }

  voucherTypeSelected(): void {
    if (this.voucherType === 'fixed') {
      this.valueFormControl.setValidators([
        Validators.required,
        Validators.min(0),
      ]);
      this.costFormControl.setValidators([
        Validators.required,
        Validators.min(this.minimumCost),
      ]);
      this.minFormControl.clearValidators();
      this.maxFormControl.clearValidators();
      this.incrementFormControl.clearValidators();
    } else {
      this.valueFormControl.clearValidators();
      this.costFormControl.clearValidators();

      this.minFormControl.setValidators([
        Validators.required,
        Validators.min(this.minValue),
      ]);
      this.maxFormControl.setValidators([
        Validators.required,
        Validators.min(this.minValue),
      ]);
      this.incrementFormControl.setValidators([
        Validators.required,
        Validators.min(1),
      ]);
    }

    this.valueFormControl.updateValueAndValidity();
    this.costFormControl.updateValueAndValidity();
    this.minFormControl.updateValueAndValidity();
    this.maxFormControl.updateValueAndValidity();
    this.incrementFormControl.updateValueAndValidity();

    // Update the form validity as well
    this.form.updateValueAndValidity();

    // Mark controls as touched if form has been submitted to show validation errors
    if (this.submitted) {
      this.valueFormControl.markAsTouched();
      this.costFormControl.markAsTouched();
      this.minFormControl.markAsTouched();
      this.maxFormControl.markAsTouched();
      this.incrementFormControl.markAsTouched();
    }
  }

  get voucherType(): string {
    return this.voucherTypeFormControl.value?.value;
  }
}
